import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  PhoneCall, MessageSquare, ArrowRight, ExternalLink,
  HelpCircle, FileText, Send, Shield, AlertCircle,
  Plus, MessageCircle, RefreshCw, Lock, Check, XCircle
} from 'lucide-react';

const DoxezSupport = () => {
  const { user } = useAuth();
  const { socket } = useSocket() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  // Creation form states
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [caseId, setCaseId] = useState(searchParams.get('caseId') || '');
  const [submitting, setSubmitting] = useState(false);
  const [caseDetails, setCaseDetails] = useState(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);

  // Tickets & Chat states
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  // Typing indicator states
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [typingUserName, setTypingUserName] = useState('');
  const typingTimeoutRef = useRef(null);
  const isCurrentlyTypingRef = useRef(false);

  const faqs = [
    {
      q: "How long does it take for payout settlements?",
      a: "Surgical fee payouts are typically processed within 24 to 48 hours of surgical completion. Ensure that both the Surgeon and anesthesiologist have successfully entered their completion verification OTPs to trigger the automated settlement pipeline."
    },
    {
      q: "What should I do if the surgery start OTP verification fails?",
      a: "If the OT code verification fails, confirm you are entering the specific code allocated to your role (Surgeon vs anesthesiologist). If issues persist, the hospital admin can regenerate the OTP codes from their dashboard or you can contact our Clinical Operations desk instantly."
    },
    {
      q: "How can I update my professional attachments (Registration certificate, Pan)?",
      a: "You can update all uploaded attachments directly in your Profile page. If your profile is already verified and locked, please submit a support ticket below with the document details, and our verification team will update it for you."
    },
    {
      q: "Can I schedule cases on short notice?",
      a: "Yes. Doxez supports emergency attachments and prompt scheduling. Please update your working window and availability preference model (e.g. 2 Hours notice preference) in your Availability profile so matching leads are routed to you instantly."
    }
  ];

  const messagesEndRef = useRef(null);
  const typingScrollRef = useRef(null);
  const chatContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  const [userCases, setUserCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);

  // Fetch both assigned surgeries AND leads to populate the Case/Booking dropdown
  const fetchCasesAndLeads = async () => {
    if (!user?._id && !user?.id) return; // Wait until user is authenticated
    setLoadingCases(true);
    try {
      const [surgeriesRes, leadsRes] = await Promise.all([
        api.get('/surgeries?page=1&limit=500').catch(() => ({ data: { success: false } })),
        api.get('/leads?page=1&limit=500&sort=createdAt:desc').catch(() => ({ data: { success: false } }))
      ]);

      // Map by Lead ID to deduplicate Lead + Surgery pairs
      const caseMap = new Map();

      // 1. Process Leads
      if (leadsRes.data?.success) {
        const leads = leadsRes.data.data?.leads || [];
        leads.forEach(l => {
          const leadId = l._id.toString();
          caseMap.set(leadId, {
            _id: l._id,
            _type: 'lead',
            _displayId: l.bookingId || l._id.toString().slice(-8).toUpperCase(),
            _patientName: l.patientName || 'No Patient',
            _treatment: l.treatmentRequired || l.subServiceId?.name || 'Consultation',
            _status: l.status?.replace(/_/g, ' ').toUpperCase(),
            _paymentMode: l.hasAyushmanCard ? 'AYUSHMAN' : l.paymentType?.includes('Insurance') ? 'INSURANCE' : 'CASH',
            _patientPhone: l.patientPhone || '',
            _leadId: leadId
          });
        });
      }

      // 2. Process Surgeries (Overwrites Leads if they share the same Lead ID)
      if (surgeriesRes.data?.success) {
        const sData = surgeriesRes.data.data;
        const surgeries = Array.isArray(sData?.surgeries) ? sData.surgeries : Array.isArray(sData) ? sData : [];

        surgeries.forEach(s => {
          const leadIdStr = s.lead?._id?.toString() || s.lead?.toString();
          const existingLead = leadIdStr ? caseMap.get(leadIdStr) : null;

          caseMap.set(leadIdStr || s._id.toString(), {
            _id: s._id,
            _type: 'surgery',
            _displayId: s.bookingId || s._id.toString().slice(-8).toUpperCase(),
            _patientName: s.lead?.patientName || existingLead?._patientName || 'No Patient',
            _treatment: s.surgeryName || s.surgeryType || 'Surgery',
            _status: s.status?.replace(/_/g, ' ').toUpperCase(),
            _paymentMode: s.lead?.hasAyushmanCard ? 'AYUSHMAN' : s.lead?.paymentType?.includes('Insurance') ? 'INSURANCE' : (existingLead?._paymentMode || 'CASH'),
            _patientPhone: s.lead?.patientPhone || existingLead?._patientPhone || '',
            _leadId: leadIdStr || ''
          });
        });
      }

      const finalCases = Array.from(caseMap.values());
      setUserCases(finalCases);
    } catch (err) {
      console.error('[Support] Failed to load cases/leads:', err);
    } finally {
      setLoadingCases(false);
    }
  };

  // Fetch tickets list and cases on mount AND when user changes
  useEffect(() => {
    fetchTickets();
    fetchCasesAndLeads();
  }, [user?._id, user?.id]); // re-run when auth user is ready


  // Listen for typing events from other party
  useEffect(() => {
    if (!socket || !selectedTicketId) return;

    const handleTyping = ({ ticketId, isTyping, senderName }) => {
      if (ticketId !== selectedTicketId) return;
      setOtherUserTyping(isTyping);
      if (isTyping) {
        setTypingUserName(senderName || 'Someone');
        // Auto-clear after 4s in case stop event is missed
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setOtherUserTyping(false), 4000);
      } else {
        clearTimeout(typingTimeoutRef.current);
      }
    };

    socket.on('ticket_typing', handleTyping);
    return () => {
      socket.off('ticket_typing', handleTyping);
      setOtherUserTyping(false);
    };
  }, [socket, selectedTicketId]);

  // Scroll chat container to bottom when typing indicator appears (scoped to chat only)
  useEffect(() => {
    if (otherUserTyping && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [otherUserTyping]);

  // Emit typing events to the server
  const emitTyping = useCallback((isTyping) => {
    if (!socket || !selectedTicketId) return;
    socket.emit('ticket_typing', {
      ticketId: selectedTicketId,
      isTyping,
      senderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User',
      senderId: user?.id || user?._id
    });
  }, [socket, selectedTicketId, user]);

  const handleReplyInputChange = (e) => {
    setReplyMessage(e.target.value);
    if (!isCurrentlyTypingRef.current) {
      isCurrentlyTypingRef.current = true;
      emitTyping(true);
    }
    // Debounce: emit stop after 2.5s of inactivity
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isCurrentlyTypingRef.current = false;
      emitTyping(false);
    }, 2500);
  };

  // Fetch ticket details & start polling when a ticket is selected
  useEffect(() => {
    if (selectedTicketId) {
      fetchSingleTicket(selectedTicketId);

      const interval = setInterval(() => {
        fetchSingleTicket(selectedTicketId);
      }, 5000); // Poll every 5 seconds for new messages

      return () => clearInterval(interval);
    } else {
      setActiveTicket(null);
    }
  }, [selectedTicketId]);

  // Fetch context details when caseId/ticketId in URL changes
  useEffect(() => {
    const qCaseId = searchParams.get('caseId');
    const qType = searchParams.get('type');
    const qTicketId = searchParams.get('ticketId');

    if (qTicketId) {
      setSelectedTicketId(qTicketId);
    }

    if (qCaseId && qType) {
      fetchCaseDetails(qCaseId, qType);
    } else if (qCaseId) {
      setCaseId(qCaseId);
    }
  }, [searchParams]);

  // Scroll to bottom of chat ONLY when new messages actually arrive (scoped to chat container only)
  const currentMessageCount = activeTicket?.messages?.length || 0;
  useEffect(() => {
    if (currentMessageCount > prevMessageCountRef.current && currentMessageCount > 0 && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    prevMessageCountRef.current = currentMessageCount;
  }, [currentMessageCount]);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/support/tickets');
      if (res.data?.success) {
        setTickets(res.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchSingleTicket = async (id) => {
    try {
      const res = await api.get(`/support/tickets/${id}`);
      if (res.data?.success) {
        setActiveTicket(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
    }
  };

  const fetchCaseDetails = async (id, entityType) => {
    setFetchingDetails(true);
    try {
      if (entityType === 'surgery') {
        const res = await api.get(`/surgeries/${id}`);
        if (res.data?.success && res.data?.data) {
          const s = res.data.data;
          setCaseDetails({
            entityType: 'surgery',
            bookingId: s.bookingId || '',
            leadId: s.lead?.bookingId || s.lead?._id?.slice(-8).toUpperCase() || '',
            surgeryName: s.surgeryName || s.surgeryType || '',
            patientName: s.lead?.patientName || '',
            patientPhone: s.lead?.patientPhone || ''
          });
          setCaseId(s.bookingId || '');
          setSubject(`Assistance for Surgery #${s.bookingId || ''} (${s.surgeryName || ''})`);
        }
      } else if (entityType === 'lead') {
        const res = await api.get(`/leads/${id}`);
        if (res.data?.success && res.data?.data) {
          const l = res.data.data;
          setCaseDetails({
            entityType: 'lead',
            bookingId: l.bookingId || l._id?.slice(-8).toUpperCase() || '',
            leadId: l.bookingId || l._id?.slice(-8).toUpperCase() || '',
            surgeryName: l.clinicalData?.surgeryName || l.clinicalData?.surgeryType || 'TBD',
            patientName: l.patientName || '',
            patientPhone: l.patientPhone || ''
          });
          setCaseId(l.bookingId || l._id?.slice(-8).toUpperCase() || '');
          setSubject(`Assistance for Lead #${l.bookingId || l._id?.slice(-8).toUpperCase() || ''}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch support case contextual details:', err);
    } finally {
      setFetchingDetails(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in the subject and message details.');
      return;
    }

    const waMessage = `*New Doxez Support Ticket*
*Name*: ${user?.firstName || ''} ${user?.lastName || ''}
*Role*: ${user?.role === 'marketing_agent' ? 'Lead Partner' : user?.role || 'User'}
*Email*: ${user?.email || ''}
*Subject*: ${subject.trim()}
${caseId.trim() ? `*Booking ID*: ${caseId.trim()}` : ''}
${caseDetails?.leadId ? `*Lead ID*: ${caseDetails.leadId}` : ''}
${caseDetails?.surgeryName ? `*Surgery Name*: ${caseDetails.surgeryName}` : ''}
${caseDetails?.patientName ? `*Patient Name*: ${caseDetails.patientName}` : ''}
*Details*: ${message.trim()}`;

    setSubmitting(true);
    try {
      const res = await api.post('/support/ticket', {
        subject,
        message,
        caseId: caseId.trim() || undefined,
        bookingId: caseDetails?.bookingId || undefined,
        leadId: caseDetails?.leadId || undefined,
        surgeryName: caseDetails?.surgeryName || undefined,
        patientName: caseDetails?.patientName || undefined,
        userRole: user?.role,
        userEmail: user?.email,
        userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      });

      toast.success('Support ticket submitted successfully!');

      // Clear search params
      setSearchParams({});
      setSubject('');
      setMessage('');
      setCaseId('');
      setCaseDetails(null);

      // Re-fetch tickets list
      fetchTickets();

      // Auto-open the newly created ticket if returned
      if (res.data?.data?._id) {
        setSelectedTicketId(res.data.data._id);
      }
    } catch (error) {
      console.warn('Backend support endpoint failed, resolving gracefully locally...', error);
      setTimeout(() => {
        toast.error('Failed to submit ticket via system. Please try again.');
        // We removed the fallback WhatsApp redirect to keep the user on the platform.
        setSubject('');
        setMessage('');
        setCaseId('');
        setCaseDetails(null);
        fetchTickets();
      }, 800);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    // Stop typing indicator immediately on send
    clearTimeout(typingTimeoutRef.current);
    if (isCurrentlyTypingRef.current) {
      isCurrentlyTypingRef.current = false;
      emitTyping(false);
    }

    setSendingReply(true);
    try {
      const res = await api.post(`/support/tickets/${selectedTicketId}/message`, {
        message: replyMessage
      });
      if (res.data?.success) {
        setActiveTicket(res.data.data);
        setReplyMessage('');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await api.patch(`/support/tickets/${selectedTicketId}/status`, {
        status: newStatus
      });
      if (res.data?.success) {
        setActiveTicket(res.data.data);
        toast.success(`Ticket marked as ${newStatus}`);
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleWhatsAppChat = () => {
    const text = encodeURIComponent(`Hello Doxez Support Desk, I am Dr./Mr./Ms. ${user?.firstName || ''} ${user?.lastName || ''} (${user?.role || 'user'}). I need assistance regarding my Doxez account.`);
    window.open(`https://wa.me/+919692949500?text=${text}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-amber-100 text-amber-800 border-amber-200',
      assigned: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      closed: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto py-4">
      {/* Premium Header */}
      <div className="bg-gradient-to-r from-[#143E78] to-[#1e293b] rounded-[32px] p-8 md:p-10 text-white shadow-xl relative overflow-hidden">
        <div className="absolute right-0 bottom-0 top-0 w-1/3 bg-blue-500/10 rounded-l-full blur-3xl pointer-events-none" />
        <div className="relative z-10 space-y-3 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full backdrop-blur-md">
            <Shield size={14} className="text-blue-300 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-200">Doxez Operations Desk</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black tracking-tight leading-tight">
            How can we help you?
          </h1>
          <p className="text-gray-300 text-xs font-medium leading-relaxed max-w-xl">
            Welcome to the Doxez Operations Desk. Submit a ticket, view dynamic case mappings, and chat instantly with our Customer Care (CC) agents.
          </p>
        </div>
      </div>

      {/* Main split grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* Left Column: Ticket History panel */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-6 rounded-[28px] border border-gray-100 shadow-sm flex flex-col min-h-[500px]">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider">Your Tickets</h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedTicketId(null);
                  setSubject('');
                  setMessage('');
                  setCaseId('');
                  setCaseDetails(null);
                  setSearchParams({});
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95"
              >
                <Plus size={12} /> New Request
              </button>
            </div>

            {loadingTickets ? (
              <div className="flex-1 flex items-center justify-center text-xs font-bold text-gray-400">
                <RefreshCw className="animate-spin w-4 h-4 mr-2" /> Loading tickets...
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-2">
                <MessageCircle className="w-8 h-8 text-gray-300" />
                <p className="text-xs font-bold text-gray-400">No support tickets found</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2.5 max-h-[480px] pr-1 mt-4">
                {tickets.map((t) => {
                  const isActive = selectedTicketId === t._id;
                  return (
                    <button
                      key={t._id}
                      type="button"
                      onClick={() => setSelectedTicketId(t._id)}
                      className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col gap-2 relative group ${isActive
                          ? 'bg-[#143E78] text-white border-[#143E78] shadow-lg shadow-blue-900/10'
                          : 'bg-slate-50/50 hover:bg-slate-50 border-gray-100 text-slate-700'
                        }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[9px] font-mono font-bold ${isActive ? 'text-blue-200' : 'text-slate-400'}`}>
                          {t.ticketId}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${getStatusBadge(t.status)}`}>
                          {t.status}
                        </span>
                      </div>
                      <h4 className="text-xs font-black line-clamp-1 leading-snug">{t.subject}</h4>
                      <div className="flex items-center justify-between text-[9px] font-bold mt-1 opacity-70">
                        <span>{new Date(t.createdAt).toLocaleDateString([], { day: '2-digit', month: 'short' })}</span>
                        <span>{t.assignedTo ? `CC: ${t.assignedTo.firstName}` : 'Unassigned'}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Contact Buttons */}
          <div className="bg-white p-6 rounded-[28px] border border-gray-100 shadow-sm space-y-3.5">
            <h3 className="text-xs font-black text-gray-900 uppercase tracking-wider mb-1">Direct Desk</h3>

            {/* WhatsApp */}
            <button
              onClick={handleWhatsAppChat}
              className="w-full flex items-center justify-between p-3.5 bg-emerald-50 hover:bg-emerald-100/85 rounded-2xl border border-emerald-100 group transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center shadow flex-shrink-0">
                  <MessageSquare size={16} />
                </div>
                <div>
                  <p className="text-[11px] font-black text-emerald-950 uppercase tracking-tight">WhatsApp Support</p>
                  <p className="text-[9px] font-bold text-emerald-600">Immediate chat response</p>
                </div>
              </div>
              <ExternalLink size={14} className="text-emerald-500 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Phone */}
            <a
              href="tel:+919692949500"
              className="w-full flex items-center justify-between p-3.5 bg-blue-50 hover:bg-blue-100/85 rounded-2xl border border-blue-100 group transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow flex-shrink-0">
                  <PhoneCall size={16} />
                </div>
                <div>
                  <p className="text-[11px] font-black text-blue-950 uppercase tracking-tight">Call Operations</p>
                  <p className="text-[9px] font-bold text-blue-600">+91 9692949500</p>
                </div>
              </div>
              <ArrowRight size={14} className="text-blue-500 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>

        {/* Right Column: Live Chat Panel OR Creation Form */}
        <div className="lg:col-span-2 space-y-6">
          {selectedTicketId ? (
            /* ==================== 1. CHAT THREAD VIEW ==================== */
            <div className="bg-white rounded-[28px] border border-gray-100 shadow-sm overflow-hidden flex flex-col min-h-[600px] max-h-[680px]">

              {/* Chat Header */}
              {activeTicket ? (
                <div className="bg-gray-50 border-b border-gray-100 p-4 sm:p-5 flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                        {activeTicket.ticketId}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider border ${getStatusBadge(activeTicket.status)}`}>
                        {activeTicket.status}
                      </span>
                    </div>
                    <h2 className="text-sm sm:text-base font-black text-gray-900 mt-1 leading-snug">{activeTicket.subject}</h2>
                    {activeTicket.assignedTo ? (
                      <p className="text-[10px] font-bold text-gray-400 mt-0.5">
                        Assigned Coordinator: <span className="text-[#143E78]">{activeTicket.assignedTo.firstName} {activeTicket.assignedTo.lastName}</span>
                      </p>
                    ) : (
                      <p className="text-[10px] font-bold text-amber-500 mt-0.5 flex items-center gap-1">
                        <AlertCircle size={10} /> Auto-assigning to active CC agent...
                      </p>
                    )}
                  </div>

                  {/* Status Management */}
                  {['admin', 'customer_care', 'cc_head'].includes(user?.role) ? (
                    <div className="flex gap-2">
                      {/* Mark Resolved: visible only when status is open or assigned */}
                      {['open', 'assigned'].includes(activeTicket.status) && (
                        <button
                          onClick={() => handleUpdateStatus('resolved')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-100 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                        >
                          <Check size={12} /> Mark Resolved
                        </button>
                      )}
                      {/* Close Ticket: visible when status is NOT closed */}
                      {activeTicket.status !== 'closed' && (
                        <button
                          onClick={() => handleUpdateStatus('closed')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                        >
                          <XCircle size={12} /> Close Ticket
                        </button>
                      )}
                      {/* Reopen Ticket: visible when status is closed */}
                      {activeTicket.status === 'closed' && (
                        <button
                          onClick={() => handleUpdateStatus(activeTicket.assignedTo ? 'assigned' : 'open')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-100 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                        >
                          <RefreshCw size={12} /> Reopen Ticket
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      {/* User close button */}
                      {activeTicket.status !== 'closed' && activeTicket.createdBy?._id === user?.id && (
                        <button
                          onClick={() => handleUpdateStatus('closed')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                        >
                          <XCircle size={12} /> Close Ticket
                        </button>
                      )}
                      {/* User reopen button */}
                      {activeTicket.status === 'closed' && activeTicket.createdBy?._id === user?.id && (
                        <button
                          onClick={() => handleUpdateStatus(activeTicket.assignedTo ? 'assigned' : 'open')}
                          disabled={updatingStatus}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-100 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all active:scale-95 disabled:opacity-50 cursor-pointer"
                        >
                          <RefreshCw size={12} /> Reopen Ticket
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-5 border-b border-gray-100 text-xs font-bold text-gray-400">
                  Loading details...
                </div>
              )}

              {/* Message thread body */}
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-5 sm:p-6 bg-slate-50/50 space-y-4 max-h-[420px] min-h-[350px]">
                {activeTicket?.messages?.map((msg, index) => {
                  const isMe = msg.sender === user.id;
                  return (
                    <div key={msg._id || index} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                      <span className="text-[9px] font-bold text-slate-400 mb-1 px-1">
                        {isMe ? 'You' : msg.senderName}
                      </span>
                      <div className={`p-3.5 rounded-2xl max-w-[85%] text-xs font-medium shadow-sm leading-relaxed ${isMe
                          ? 'bg-[#143E78] text-white rounded-tr-none'
                          : 'bg-white text-slate-800 border border-gray-100 rounded-tl-none'
                        }`}>
                        <p className="whitespace-pre-wrap">{msg.message}</p>
                      </div>
                      <span className="text-[8px] font-black text-slate-300 mt-1 uppercase tracking-tighter px-1">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  );
                })}
                {/* Typing Indicator */}
                {otherUserTyping && (
                  <div className="flex flex-col items-start" ref={typingScrollRef}>
                    <span className="text-[9px] font-bold text-slate-400 mb-1 px-1">{typingUserName}</span>
                    <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                    <span className="text-[8px] font-black text-slate-300 mt-1 uppercase tracking-tighter px-1">typing...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply controls */}
              <div className="p-4 border-t border-gray-100 bg-white">
                {activeTicket?.status === 'closed' ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-center gap-2 text-slate-500 font-bold text-xs">
                    <Lock size={14} />
                    <span>This ticket is closed. Submit a new request for help.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSendReply} className="flex gap-2">
                    <input
                      type="text"
                      value={replyMessage}
                      onChange={handleReplyInputChange}
                      onBlur={() => {
                        // Stop typing when input loses focus
                        clearTimeout(typingTimeoutRef.current);
                        if (isCurrentlyTypingRef.current) {
                          isCurrentlyTypingRef.current = false;
                          emitTyping(false);
                        }
                      }}
                      placeholder="Type your message here..."
                      className="flex-1 px-4 py-3 rounded-xl bg-gray-50 border border-gray-100 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all"
                    />
                    <button
                      type="submit"
                      disabled={sendingReply || !replyMessage.trim()}
                      className="px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center justify-center shadow-md active:scale-95 disabled:opacity-50 transition-all cursor-pointer"
                    >
                      <Send size={14} />
                    </button>
                  </form>
                )}
              </div>

            </div>
          ) : (
            /* ==================== 2. TICKET SUBMISSION FORM ==================== */
            <div className="bg-white p-8 rounded-[28px] border border-gray-100 shadow-sm space-y-6">
              <div>
                <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
                  <FileText className="text-blue-600" />
                  Submit a Support Request
                </h2>
                <p className="text-gray-400 text-xs font-medium mt-1">
                  Fill in the details below, and our CRM auto-router will assign an agent to review your request.
                </p>
              </div>

              <form onSubmit={handleSubmitTicket} className="space-y-4">
                {fetchingDetails && (
                  <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 text-xs font-bold text-blue-600 animate-pulse flex items-center gap-2">
                    <span>⌛</span> Fetching context details...
                  </div>
                )}

                {!fetchingDetails && caseDetails && (
                  <div className="bg-gradient-to-br from-indigo-50/50 to-blue-50/50 border border-indigo-100/80 rounded-2xl p-4 sm:p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black text-indigo-600 uppercase tracking-widest bg-indigo-50 px-2.5 py-0.5 rounded-md">
                        Linked {caseDetails.entityType === 'surgery' ? 'Surgery Case' : 'Lead Details'}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setCaseDetails(null);
                          setCaseId('');
                          setSubject('');
                        }}
                        className="text-[9px] font-black text-slate-400 hover:text-red-500 uppercase tracking-tighter"
                      >
                        Clear Linked Case
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-xs font-bold">
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Booking ID</p>
                        <p className="text-slate-800 font-mono">#{caseDetails.bookingId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Lead ID</p>
                        <p className="text-slate-600 font-mono">#{caseDetails.leadId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Surgery Name</p>
                        <p className="text-[#143E78]">{caseDetails.surgeryName || 'TBD'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Patient Name</p>
                        <p className="text-slate-800">{caseDetails.patientName || 'TBD'}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Subject / Topic</label>
                    <input
                      type="text"
                      required
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="e.g. Payout pending for Surgery #1205"
                      className="w-full px-4 py-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">
                      Case / Booking ID (Optional)
                      {loadingCases && <span className="ml-2 text-blue-400 normal-case tracking-normal font-semibold">Loading...</span>}
                    </label>

                    <select
                      value={caseId}
                      onChange={(e) => {
                        const selectedVal = e.target.value;
                        setCaseId(selectedVal);

                        if (selectedVal) {
                          const found = userCases.find(c => c._displayId === selectedVal);
                          if (found) {
                            setCaseDetails({
                              entityType: found._type,
                              bookingId: found._displayId,
                              leadId: found._leadId || found._displayId,
                              surgeryName: found._treatment,
                              patientName: found._patientName,
                              patientPhone: found._patientPhone
                            });
                            setSubject(`Assistance for ${found._type === 'surgery' ? 'Surgery' : 'Case'} #${found._displayId} — ${found._patientName}`);
                          } else {
                            setCaseDetails(null);
                          }
                        } else {
                          setCaseDetails(null);
                          setSubject('');
                        }
                      }}
                      className="w-full px-4 py-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all cursor-pointer text-[#143E78]"
                    >
                      <option value="">— Select a Surgery, Case, or Lead —</option>
                      {userCases.length > 0 ? (
                        userCases.map((c) => (
                          <option key={`${c._type}-${c._id}`} value={c._displayId}>
                            #{c._displayId} — {c._patientName} ({c._treatment}) [{c._status}] — {c._paymentMode}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>— No Surgeries, Cases, or Leads Found —</option>
                      )}
                    </select>
                  </div>

                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Detailed Message</label>
                  <textarea
                    required
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Provide all details, including patients name, dates, or payment ID if applicable..."
                    className="w-full px-4 py-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all disabled:opacity-50 cursor-pointer"
                >
                  <Send size={14} />
                  {submitting ? 'Submitting query...' : 'Submit Support Request'}
                </button>
              </form>
            </div>
          )}
        </div>

      </div>

      {/* Frequently Asked Questions (FAQ) */}
      <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
        <div>
          <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
            <HelpCircle className="text-[#143E78]" />
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-xs font-medium mt-1">
            Browse through quick operational guidelines for surgeons, anesthetists, and hospital partners.
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {faqs.map((faq, index) => {
            const isOpen = activeFaq === index;
            return (
              <div key={index} className="py-4 first:pt-0 last:pb-0">
                <button
                  onClick={() => setActiveFaq(isOpen ? null : index)}
                  className="w-full flex items-center justify-between text-left focus:outline-none"
                >
                  <span className="text-xs font-black text-gray-800 tracking-tight hover:text-blue-600 transition-colors">
                    {faq.q}
                  </span>
                  <span className={`text-[#143E78] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {isOpen && (
                  <div className="mt-3 text-[11px] font-bold text-gray-500 leading-relaxed pl-1 animate-in fade-in slide-in-from-top-1 duration-200">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DoxezSupport;
